import { useMemo } from 'react';
import flatten from 'lodash/flatten';
import useAuth from '@sdk/authentication/useAuth';
import { TimesheetEntry } from '@sdk/timesheets/types/TimesheetEntry';
import { getAutomatedBreaksSettingsForShift } from '@sdk/automatedBreaks/utils';

import {
  TimeClockEntryWithDates,
  MaybeBreakAttestations,
} from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.types';
import {
  GetSummaryProps,
  UseDurationRowProps,
} from '@web/timesheets/CreateTimesheetModal/components/rows/DurationRow/durationRow.types';
import { mapTimeClockEntryWithDateToTimesheetEntry } from '@web/timesheets/CreateTimesheetModal/utils/mappers';
import { useAutomatedBreaksSettings } from '@web/settings/components/schedule/Breaks/breaks.hooks';
import { getSummary } from '@web/timesheets/CreateTimesheetModal/components/rows/DurationRow/durationRow.utils';
import useAutomatedBreaksEnabled from '@web/timeClock/hooks/useAutomatedBreaksEnabled';
import {
  getAllBreakEntriesWithDates,
  getTotalBreaksDurationInMinutes,
} from '@web/timesheets/CreateTimesheetModal/utils/breakUtils';
import {
  getTimeClockEntriesWithDates,
  calculateUnpaidBreakMinutes,
} from '@web/timesheets/CreateTimesheetModal/utils/timeClockUtils';
import useIsAutomatedBreaksFeatureFlagEnabled from '@web/automatedBreaks/hooks/useIsAutomatedBreaksFeatureFlagEnabled';
import { ShiftWithLocationTimezone } from '@web/timeClock/ClockOutModal/clockOutModal.types';

export const useDurationRow = ({ timeClockEntries, entriesDate, shift }: UseDurationRowProps) => {
  const timeClockEntriesWithDates = getTimeClockEntriesWithDates(
    timeClockEntries,
    entriesDate
  ).filter(({ deleted }) => !deleted);
  const automatedBreaksFeatureEnabled = useIsAutomatedBreaksFeatureFlagEnabled();
  const areAutomatedBreaksUsed = useAutomatedBreaksEnabled();
  const { automatedBreaks } = useAutomatedBreaksSettings();
  const { currentUser } = useAuth();

  const allBreakEntriesWithDates = getAllBreakEntriesWithDates(timeClockEntriesWithDates).filter(
    ({ deleted }) => !deleted
  );
  const shouldShowBreakDuration = Boolean(
    !areAutomatedBreaksUsed && allBreakEntriesWithDates.length
  );
  const breakDurationInMintues = getTotalBreaksDurationInMinutes(allBreakEntriesWithDates);

  const unpaidBreakMinutes = calculateUnpaidBreakMinutes({
    shift,
    currentUser,
    entriesDate,
    timeClockEntries,
    automatedBreaks,
    automatedBreaksFeatureEnabled,
    breakAttestations: flatten(shift?.timesheetProjections?.map((p) => p.breakAttestations || [])),
  });

  return {
    areAutomatedBreaksUsed,
    timeClockEntriesWithDates,
    shouldShowBreakDuration,
    breakDurationInMintues,
    unpaidBreakMinutes,
  };
};

export const useAutomatedBreaksSummary = (
  shift: ShiftWithLocationTimezone | undefined,
  timeClockEntriesWithDates: TimeClockEntryWithDates[],
  breakAttestations?: MaybeBreakAttestations
) => {
  const { automatedBreaks } = useAutomatedBreaksSettings();
  const automatedBreaksSettings = shift
    ? getAutomatedBreaksSettingsForShift(shift, automatedBreaks)
    : automatedBreaks;
  // For sake of break summary we only take filled entries, which means both with start and end times.
  const { timeClockEntries, timesheetEntries }: GetSummaryProps = useMemo(() => {
    const filledTimeClockEntries = timeClockEntriesWithDates.filter(
      ({ clockIn, clockOut }) => clockIn && clockOut
    );

    const filledTimesheetEntries: Record<string, TimesheetEntry[]> = filledTimeClockEntries.reduce(
      (acc, entry) => {
        acc[entry.key] = mapTimeClockEntryWithDateToTimesheetEntry(entry, automatedBreaksSettings);
        return acc;
      },
      {}
    );

    return { timeClockEntries: filledTimeClockEntries, timesheetEntries: filledTimesheetEntries };
  }, [timeClockEntriesWithDates]);

  return getSummary({ timeClockEntries, timesheetEntries, breakAttestations });
};
